import { IAttachment } from '@app/models/Attachment'
import { CityProfile, ICityProfile } from '@app/models/CityProfile'
import {
  EntityPermissions,
  IEntityPermissions,
} from '@app/models/EntityPermissions'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface ICityViewState {
  readonly error?: string
  readonly city: ICityProfile
  readonly isLoading: boolean
  readonly isUpdatingDemographics: boolean
  readonly permissions: IEntityPermissions
}

const CityViewStateRecord = Record({
  city: new CityProfile(),
  error: undefined,
  isLoading: false,
  isUpdatingDemographics: false,
  permissions: new EntityPermissions(),
})

export class CityViewState extends CityViewStateRecord
  implements ICityViewState {
  public static fromJS(initialState?: Partial<ICityViewState>) {
    if (!initialState) {
      return new CityViewState()
    }

    const { city, permissions, ...rest } = initialState

    return new CityViewState({
      city: CityProfile.fromJS(city),
      permissions: EntityPermissions.fromJS(permissions),
      ...rest,
    })
  }

  public readonly error?: string
  public readonly city: CityProfile
  public readonly isLoading: boolean
  public readonly isUpdatingDemographics: boolean
  public readonly permissions: EntityPermissions

  constructor(props?: Partial<CityViewState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  LoadCity = 'CITY_VIEW::LOAD_CITY',
  LoadCityFailure = 'CITY_VIEW::LOAD_CITY_FAILURE',
  LoadCityRequest = 'CITY_VIEW::LOAD_CITY_REQUEST',
  LoadCitySuccess = 'CITY_VIEW::LOAD_CITY_SUCCESS',
  ResetState = 'CITY_VIEW::RESET_STATE',
  SetAttachments = 'CITY_VIEW::SET_ATTACHMENTS',
  UpdateDemographics = 'CITY_VIEW::UPDATE_DEMOGRAPHICS',
  UpdateDemographicsFailure = 'CITY_VIEW::UPDATE_DEMOGRAPHICS_FAILURE',
  UpdateDemographicsRequest = 'CITY_VIEW::UPDATE_DEMOGRAPHICS_REQUEST',
  UpdateDemographicsSuccess = 'CITY_VIEW::UPDATE_DEMOGRAPHICS_SUCCESS',
}

interface ILoadCityAction {
  citySlug: string
  type: ActionTypes.LoadCity
}

interface ILoadCityFailureAction {
  error: string
  type: ActionTypes.LoadCityFailure
}

interface ILoadCityRequestAction {
  type: ActionTypes.LoadCityRequest
}

interface ILoadCitySuccessAction {
  city: CityProfile
  permissions: EntityPermissions
  type: ActionTypes.LoadCitySuccess
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISetAttachmentsAction {
  type: ActionTypes.SetAttachments
  value: List<IAttachment>
}

interface IUpdateDemographicsAction {
  cityId: string
  type: ActionTypes.UpdateDemographics
}

interface IUpdateDemographicsFailureAction {
  error: string
  type: ActionTypes.UpdateDemographicsFailure
}

interface IUpdateDemographicsRequestAction {
  type: ActionTypes.UpdateDemographicsRequest
}

interface IUpdateDemographicsSuccessAction {
  city: CityProfile
  type: ActionTypes.UpdateDemographicsSuccess
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadCityFailureAction
  | ILoadCityRequestAction
  | ILoadCitySuccessAction
  | IResetStateAction
  | ISetAttachmentsAction
  | IUpdateDemographicsFailureAction
  | IUpdateDemographicsRequestAction
  | IUpdateDemographicsSuccessAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadCity(citySlug: string): ILoadCityAction {
  return {
    citySlug,
    type: ActionTypes.LoadCity,
  }
}

function loadCityFailure(error: string): ILoadCityFailureAction {
  return {
    error,
    type: ActionTypes.LoadCityFailure,
  }
}

function loadCityRequest(): ILoadCityRequestAction {
  return { type: ActionTypes.LoadCityRequest }
}

function loadCitySuccess(
  city: CityProfile,
  permissions: EntityPermissions,
): ILoadCitySuccessAction {
  return {
    city,
    permissions,
    type: ActionTypes.LoadCitySuccess,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function setAttachments(value: List<IAttachment>): ISetAttachmentsAction {
  return {
    type: ActionTypes.SetAttachments,
    value,
  }
}

function updateDemographics(cityId: string): IUpdateDemographicsAction {
  return {
    cityId,
    type: ActionTypes.UpdateDemographics,
  }
}

function updateDemographicsFailure(
  error: string,
): IUpdateDemographicsFailureAction {
  return {
    error,
    type: ActionTypes.UpdateDemographicsFailure,
  }
}

function updateDemographicsRequest(): IUpdateDemographicsRequestAction {
  return {
    type: ActionTypes.UpdateDemographicsRequest,
  }
}

function updateDemographicsSuccess(
  city: CityProfile,
): IUpdateDemographicsSuccessAction {
  return {
    city,
    type: ActionTypes.UpdateDemographicsSuccess,
  }
}

export const actionCreators = {
  loadCity,
  resetState,
  setAttachments,
  updateDemographics,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<CityViewState> = (
  state: CityViewState = new CityViewState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.LoadCityFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as CityViewState

    case ActionTypes.LoadCityRequest:
      return state.set('isLoading', true) as CityViewState

    case ActionTypes.LoadCitySuccess:
      return state
        .set('city', action.city)
        .set('error', undefined)
        .set('isLoading', false)
        .set('permissions', action.permissions) as CityViewState

    case ActionTypes.ResetState:
      return new CityViewState()

    case ActionTypes.SetAttachments:
      return state.setIn(
        ['city', 'attachments'],
        action.value,
      ) as CityViewState

    case ActionTypes.UpdateDemographicsFailure:
      return state
        .set('error', action.error)
        .set('isUpdatingDemographics', false) as CityViewState

    case ActionTypes.UpdateDemographicsRequest:
      return state.set('isUpdatingDemographics', true) as CityViewState

    case ActionTypes.UpdateDemographicsSuccess:
      return state
        .set(
          'city',
          state.city.id === action.city.id ? action.city : state.city,
        )
        .set('error', undefined)
        .set('isUpdatingDemographics', false) as CityViewState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadCitySaga(action: ILoadCityAction) {
  try {
    yield put(loadCityRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/CityProfiles/${action.citySlug}`,
    )

    if (result.success === true) {
      yield put(
        loadCitySuccess(
          CityProfile.fromJS(result.entity),
          EntityPermissions.fromJS(result.permissions),
        ),
      )
    } else {
      yield put(loadCityFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadCityFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* updateDemographicsSaga(action: IUpdateDemographicsAction) {
  try {
    yield put(updateDemographicsRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/CityProfiles/${action.cityId}/Demographics`,
      { method: RequestMethod.PUT },
    )

    if (result.success === true) {
      const city = CityProfile.fromJS(result.entity)
      yield put(updateDemographicsSuccess(city))
      yield put(
        successToast(
          'Success!',
          `Demographics data updated for ${city.place.cityState}`,
        ),
      )
    } else {
      yield put(updateDemographicsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(updateDemographicsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadCity() {
  yield takeLatest(ActionTypes.LoadCity, loadCitySaga)
}

function* watchUpdateDemographics() {
  yield takeLatest(ActionTypes.UpdateDemographics, updateDemographicsSaga)
}

export function* cityViewRootSaga() {
  yield fork(watchLoadCity)
  yield fork(watchUpdateDemographics)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) => state.cityView

export const selectCity = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.city,
  )

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectIsUpdatingDemographics = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isUpdatingDemographics,
  )

export const selectPermissions = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.permissions,
  )
